import React from "react";

/**
 * Rich text renderer — allows admin to use limited HTML in content fields
 * for bold, colours and line breaks. Safe subset only.
 *
 * Supports:
 *   <b>bold</b>, <strong>bold</strong>
 *   <i>italic</i>, <em>italic</em>
 *   <u>underline</u>
 *   <span style="color:red">coloured</span>
 *   <br />
 *   Line breaks in raw text are preserved via `whitespace-pre-line` on parent.
 *
 * Anything not on this whitelist is escaped.
 */
const ALLOWED_TAGS = /<\/?(?:b|strong|i|em|u|span|br)(?:\s+[^>]*)?\s*\/?>/gi;

// Sanitize style attributes: only allow color:xxx and background-color:xxx
const sanitizeSpanStyle = (html) =>
  html.replace(/<span([^>]*?)>/gi, (match, attrs) => {
    const styleMatch = attrs.match(/style="([^"]*)"/i);
    if (!styleMatch) return "<span>";
    const safeStyles = styleMatch[1]
      .split(";")
      .map((s) => s.trim())
      .filter((s) => /^(color|background-color|font-weight):\s*[#a-zA-Z0-9,()\s.-]+$/i.test(s))
      .join("; ");
    return safeStyles ? `<span style="${safeStyles}">` : "<span>";
  });

export const RichText = ({ text = "", as: Tag = "span", className = "" }) => {
  if (!text) return null;
  // Escape everything, then re-allow whitelisted tags
  const escaped = String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  // Re-inject allowed tags (their < > were escaped, un-escape them selectively)
  let html = escaped
    .replace(/&lt;(\/?)(b|strong|i|em|u|br)(\s*\/?)&gt;/gi, "<$1$2$3>")
    .replace(/&lt;span([^&]*?)&gt;/gi, "<span$1>")
    .replace(/&lt;\/span&gt;/gi, "</span>");
  html = sanitizeSpanStyle(html);
  return <Tag className={className} dangerouslySetInnerHTML={{ __html: html }} />;
};

export default RichText;
